import React, { useMemo, useRef, useState } from "react";
import { statusPillClasses, formatDate, statusDotClasses } from "@/lib/format";
import { applyFilters } from "@/components/FilterBar";
import { AvatarGroup } from "@/components/inline/AssigneeInline";

const DAY = 24 * 3600 * 1000;
const COL_WIDTH = 36;  // px per day
const ROW_HEIGHT = 40;

function startOfDay(d) {
  const x = new Date(d); x.setHours(0, 0, 0, 0); return x;
}

function addDays(d, n) { return new Date(d.getTime() + n * DAY); }
function daysBetween(a, b) { return Math.round((startOfDay(b) - startOfDay(a)) / DAY); }

export default function GanttView({ list, tasks, users, filters, canEdit, onOpenTask, onUpdateTask }) {
  const topLevel = tasks.filter((t) => !t.parent_task_id);
  const filtered = applyFilters(topLevel, filters);
  const statusMap = Object.fromEntries((list.statuses || []).map((s) => [s.id, s]));

  // Determine date window. Use earliest start_date or due_date - 7 days, latest due_date + 14 days
  const { rangeStart, rangeDays } = useMemo(() => {
    const today = startOfDay(new Date());
    let min = today, max = addDays(today, 21);
    for (const t of filtered) {
      const s = t.start_date ? startOfDay(t.start_date) : null;
      const d = t.due_date ? startOfDay(t.due_date) : null;
      if (s && s < min) min = s;
      if (d && d < min) min = d;
      if (s && s > max) max = s;
      if (d && d > max) max = d;
    }
    min = addDays(min, -3);
    max = addDays(max, 7);
    return { rangeStart: min, rangeDays: Math.max(14, daysBetween(min, max) + 1) };
  }, [filtered]);

  const days = useMemo(() => {
    const out = [];
    for (let i = 0; i < rangeDays; i++) out.push(addDays(rangeStart, i));
    return out;
  }, [rangeStart, rangeDays]);

  const todayOffset = daysBetween(rangeStart, new Date());

  // Drag handling
  const [dragging, setDragging] = useState(null); // { taskId, mode: 'move'|'resize-start'|'resize-end', startX, originStart, originEnd }
  const containerRef = useRef(null);

  const onMouseDown = (e, task, mode) => {
    if (!canEdit) return;
    e.preventDefault(); e.stopPropagation();
    const start = task.start_date ? startOfDay(task.start_date) : (task.due_date ? startOfDay(task.due_date) : startOfDay(new Date()));
    const end = task.due_date ? startOfDay(task.due_date) : addDays(start, 0);
    setDragging({ taskId: task.id, mode, startX: e.clientX, originStart: start, originEnd: end });
  };

  const onMouseMove = (e) => {
    if (!dragging) return;
    const dx = e.clientX - dragging.startX;
    const delta = Math.round(dx / COL_WIDTH);
    if (delta === dragging.lastDelta) return;
    setDragging((d) => ({ ...d, lastDelta: delta }));
  };

  const onMouseUp = async () => {
    if (!dragging) return;
    const delta = dragging.lastDelta || 0;
    if (delta !== 0) {
      const t = filtered.find((x) => x.id === dragging.taskId);
      if (t) {
        let newStart = dragging.originStart;
        let newEnd = dragging.originEnd;
        if (dragging.mode === "move") {
          newStart = addDays(newStart, delta);
          newEnd = addDays(newEnd, delta);
        } else if (dragging.mode === "resize-start") {
          newStart = addDays(newStart, delta);
          if (newStart > newEnd) newStart = newEnd;
        } else if (dragging.mode === "resize-end") {
          newEnd = addDays(newEnd, delta);
          if (newEnd < newStart) newEnd = newStart;
        }
        await onUpdateTask(t.id, {
          start_date: newStart.toISOString(),
          due_date: newEnd.toISOString(),
        });
      }
    }
    setDragging(null);
  };

  // Compute task bar geometry (apply live delta during drag)
  const barFor = (t) => {
    let start = t.start_date ? startOfDay(t.start_date) : (t.due_date ? startOfDay(t.due_date) : null);
    let end = t.due_date ? startOfDay(t.due_date) : (t.start_date ? startOfDay(t.start_date) : null);
    if (!start && !end) return null;
    if (!start) start = end;
    if (!end) end = start;
    if (dragging && dragging.taskId === t.id) {
      const delta = dragging.lastDelta || 0;
      if (dragging.mode === "move") { start = addDays(dragging.originStart, delta); end = addDays(dragging.originEnd, delta); }
      else if (dragging.mode === "resize-start") { start = addDays(dragging.originStart, delta); if (start > end) start = end; }
      else if (dragging.mode === "resize-end") { end = addDays(dragging.originEnd, delta); if (end < start) end = start; }
    }
    const offset = daysBetween(rangeStart, start);
    const length = daysBetween(start, end) + 1;
    return { left: offset * COL_WIDTH, width: length * COL_WIDTH, start, end };
  };

  const monthLabels = useMemo(() => {
    const labels = [];
    let last = null;
    days.forEach((d, i) => {
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      if (key !== last) {
        labels.push({ idx: i, label: d.toLocaleDateString(undefined, { month: "short", year: "numeric" }) });
        last = key;
      }
    });
    return labels;
  }, [days]);

  return (
    <div
      className="bg-white border border-neutral-200 rounded-sm overflow-hidden"
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      data-testid="gantt-view"
    >
      <div className="flex">
        {/* Left: task names */}
        <div className="w-72 shrink-0 border-r border-neutral-200 bg-neutral-50/40">
          <div className="h-12 border-b border-neutral-200 px-3 flex items-center font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">Task</div>
          {filtered.length === 0 && (
            <div className="px-3 py-6 text-xs text-neutral-500 text-center">No tasks to plot.</div>
          )}
          {filtered.map((t) => {
            const st = statusMap[t.status_id];
            return (
              <button
                key={t.id}
                onClick={() => onOpenTask(t.id)}
                style={{ height: ROW_HEIGHT }}
                className="w-full px-3 flex items-center gap-2 border-b border-neutral-100 hover:bg-neutral-100 text-left transition-colors"
                data-testid={`gantt-task-name-${t.id}`}
              >
                <span className={`w-2 h-2 rounded-full shrink-0 ${statusDotClasses(st?.color || "neutral")}`} />
                <span className="text-sm truncate flex-1">{t.title}</span>
                <AvatarGroup task={t} users={users} max={2} />
              </button>
            );
          })}
        </div>

        {/* Right: timeline */}
        <div className="flex-1 overflow-x-auto" ref={containerRef}>
          <div style={{ width: rangeDays * COL_WIDTH }} className="relative">
            {/* Month header */}
            <div className="h-6 border-b border-neutral-200 relative bg-neutral-50/60">
              {monthLabels.map((m, i) => (
                <div
                  key={i}
                  className="absolute font-mono-data text-[10px] uppercase tracking-[0.15em] text-neutral-500 px-1.5"
                  style={{ left: m.idx * COL_WIDTH, top: 4 }}
                >
                  {m.label}
                </div>
              ))}
            </div>
            {/* Day header */}
            <div className="h-6 border-b border-neutral-200 flex">
              {days.map((d, i) => {
                const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                const isToday = i === todayOffset;
                return (
                  <div
                    key={i}
                    style={{ width: COL_WIDTH }}
                    className={`flex items-center justify-center text-[10px] font-mono-data border-r border-neutral-100 ${
                      isWeekend ? "bg-neutral-50 text-neutral-400" : "text-neutral-500"
                    } ${isToday ? "bg-blue-50 text-blue-700 font-bold" : ""}`}
                  >
                    {d.getDate()}
                  </div>
                );
              })}
            </div>

            {/* Rows */}
            <div className="relative">
              {/* Vertical grid + weekend bands behind bars */}
              <div className="absolute inset-0 flex pointer-events-none">
                {days.map((d, i) => {
                  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                  const isToday = i === todayOffset;
                  return (
                    <div
                      key={i}
                      style={{ width: COL_WIDTH }}
                      className={`border-r border-neutral-100 ${isWeekend ? "bg-neutral-50/60" : ""} ${isToday ? "bg-blue-50/40" : ""}`}
                    />
                  );
                })}
              </div>

              {filtered.map((t) => {
                const bar = barFor(t);
                const st = statusMap[t.status_id];
                return (
                  <div key={t.id} style={{ height: ROW_HEIGHT }} className="border-b border-neutral-100 relative">
                    {bar && (
                      <div
                        style={{ left: bar.left, width: Math.max(COL_WIDTH, bar.width), top: 6, height: ROW_HEIGHT - 12 }}
                        className={`absolute rounded-md ${statusPillClasses(st?.color || "neutral")} shadow-sm flex items-center px-2 cursor-grab active:cursor-grabbing select-none group`}
                        onMouseDown={(e) => onMouseDown(e, t, "move")}
                        onDoubleClick={() => onOpenTask(t.id)}
                        data-testid={`gantt-bar-${t.id}`}
                        title={`${formatDate(bar.start)} → ${formatDate(bar.end)}`}
                      >
                        {/* Resize handles */}
                        {canEdit && (
                          <>
                            <div
                              onMouseDown={(e) => onMouseDown(e, t, "resize-start")}
                              className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize bg-black/10 opacity-0 group-hover:opacity-100"
                              data-testid={`gantt-resize-start-${t.id}`}
                            />
                            <div
                              onMouseDown={(e) => onMouseDown(e, t, "resize-end")}
                              className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize bg-black/10 opacity-0 group-hover:opacity-100"
                              data-testid={`gantt-resize-end-${t.id}`}
                            />
                          </>
                        )}
                        <span className="text-[11px] font-semibold truncate">{t.title}</span>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Today vertical line */}
              {todayOffset >= 0 && todayOffset < rangeDays && (
                <div
                  className="absolute top-0 bottom-0 w-px bg-blue-500 pointer-events-none"
                  style={{ left: todayOffset * COL_WIDTH + COL_WIDTH / 2 }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
